import { HttpParams } from '@angular/common/http';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  IELAsyncAutocompleteElement,
  SELECTION_MODE,
} from '@el-async-autocomplete';
import { TranslateService } from '@ngx-translate/core';

import { IInvitees } from '@shared/interfaces';

import { LoggedUserService } from '../../../../auth/services';
import { IdentitiesService } from '../../../../identities/services/identities.service';
import { IFilterData } from '../../../../setup/messages/popups/add-new-message-group/add-new-group.component';
import { POPUP_DATA } from '../../calendar-booking/calendar-booking.component';

@Component({
  selector: 'app-reserve-popup',
  templateUrl: './reserve-popup.component.html',
  styleUrls: ['./reserve-popup.component.scss'],
})
export class ReservePopupDialogComponent implements OnInit {
  @ViewChild('auto', { static: false }) matAutocomplete: MatAutocomplete;
  autoComplete: MatAutocompleteTrigger;
  SELECTION_MODE = SELECTION_MODE;

  constructor(
    public dialogRef: MatDialogRef<ReservePopupDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: POPUP_DATA,
    public translate: TranslateService,
    private identitiesService: IdentitiesService,
    private loggedUserService: LoggedUserService
  ) {}

  addEditMakeABookingFormGroup: FormGroup<{
    title: FormControl<string>;
    date: FormControl<Date>;
    from: FormControl<Date>;
    to: FormControl<Date>;
  }>;

  invitees: IELAsyncAutocompleteElement[] = [];
  filteredInvitees: IInvitees[] = [];
  selectedUsers: IELAsyncAutocompleteElement[] = [];
  isInvitees = false;
  selectable = true;

  isView = false;
  isUpdate = false;

  loggedUserData: any;

  ngOnInit(): void {
    this.setFormData();
    this.getLoggedUser();

    if (this.data?.isDisabled) {
      this.isView = true;

      this.addEditMakeABookingFormGroup
        .get('title')
        .setValue(this.data?.data?.title ?? '');
      this.addEditMakeABookingFormGroup
        .get('date')
        .setValue(this.data?.data?.start ?? this.data?.date);
      this.addEditMakeABookingFormGroup
        .get('from')
        .setValue(this.data?.data?.start ?? this.data?.date);
      this.addEditMakeABookingFormGroup
        .get('to')
        .setValue(this.data?.data?.end ?? null);

      this.data.invitees.forEach((invitee) => {
        this.selectedUsers.push({
          key: invitee.id,
          value: invitee.name,
          disable: true,
        });
      });
    } else if (this.data?.data?.title) {
      this.isUpdate = true;

      this.addEditMakeABookingFormGroup
        .get('title')
        .setValue(this.data?.data?.title ?? '');
      this.addEditMakeABookingFormGroup
        .get('date')
        .setValue(this.data?.data?.start ?? this.data?.date);
      this.addEditMakeABookingFormGroup
        .get('from')
        .setValue(this.data?.data?.start ?? this.data?.date);
      this.addEditMakeABookingFormGroup
        .get('to')
        .setValue(this.data?.data?.end ?? null);

      this.filteredInvitees = this.data.invitees;
      this.data.invitees.forEach((invitee) => {
        this.selectedUsers.push({
          key: invitee.id,
          value: invitee.name,
          disable:
            invitee.id === this.loggedUserData._id?.toString() ? true : false,
        });
      });
    } else {
      this.addEditMakeABookingFormGroup.get('date').setValue(this.data?.date);
      this.addEditMakeABookingFormGroup.get('from').setValue(this.data?.date);
      this.addEditMakeABookingFormGroup
        .get('to')
        .setValue(this.addThirtyMinutes(new Date(this.data?.date)));
      this.selectedUsers.push({
        key: this.loggedUserData._id.toString(),
        value:
          this.loggedUserData.first_name +
          ' ' +
          this.loggedUserData?.last_name +
          ' ',
        disable: true,
      });
    }
  }

  getLoggedUser() {
    this.loggedUserService.dataStore.subscribe(async (change) => {
      if (change) {
        this.loggedUserData = {
          ...change,
          all_connections: change?.all_connections?.filter(
            (connection) => !connection?.app?.self
          ),
        };
      }
    });
  }

  addThirtyMinutes(datetime: Date): Date {
    const newDate = new Date(datetime.getTime());
    newDate.setMinutes(newDate.getMinutes() + 30);
    return newDate;
  }

  setFormData() {
    this.addEditMakeABookingFormGroup = new FormGroup({
      title: new FormControl('', [
        Validators.required,
        this.noWhitespaceValidator,
      ]),
      date: new FormControl(null),
      from: new FormControl(null, [Validators.required]),
      to: new FormControl(null, [
        Validators.required,
        this.checkToDateValidator,
      ]),
    });
  }

  noWhitespaceValidator(control: FormControl) {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
  }

  closeDialog(): void {
    this.dialogRef.close({
      id: this.data?.data?.id ?? '',
      title: this.addEditMakeABookingFormGroup.get('title').value ?? '',
      from: this.addEditMakeABookingFormGroup.get('from').value ?? '',
      to: this.addEditMakeABookingFormGroup.get('to').value ?? '',
      invitees: this.filteredInvitees ?? [],
    });
  }

  saveAndClose(): void {
    this.closeDialog();
  }

  selectUser(e) {
    this.filteredInvitees = [];
    e.forEach((element) => {
      this.filteredInvitees.push({
        id: element.key,
        name: element.value,
      });
    });
  }

  onFilterUsers(eventData: IFilterData) {
    if (eventData.type === 'scroll') {
      let httpParams = new HttpParams();
      httpParams = httpParams.append('name', eventData.event);
      httpParams = httpParams.append('start', eventData.start);
      httpParams = httpParams.append('size', eventData.size);
      this.identitiesService
        .getIdentitiesWithoutContactPerson(httpParams)
        .subscribe((data_all_users) => {
          const filterUserList = [];
          data_all_users.data.forEach((user) => {
            filterUserList.push({
              key: user._id,
              value: `${user?.first_name} ${
                user?.last_name ? user?.last_name : ''
              } `,
            });
          });

          this.invitees = this.invitees.concat(filterUserList);
        });
    } else if (eventData.type === 'typing') {
      let httpParams = new HttpParams();
      httpParams = httpParams.append('name', eventData.event);
      httpParams = httpParams.append('start', eventData.start);
      httpParams = httpParams.append('size', eventData.size);
      this.identitiesService
        .getIdentitiesWithoutContactPerson(httpParams)
        .subscribe((data_all_users) => {
          const filterUserList = [];
          data_all_users.data.forEach((user) => {
            filterUserList.push({
              key: user._id,
              value: `${user?.first_name} ${
                user?.last_name ? user?.last_name : ''
              } `,
            });
          });

          this.invitees = filterUserList;
        });
    }
  }

  optionPanelOpen() {
    this.autoComplete?.openPanel();
  }

  findIndexById(array: IInvitees[], id: string): number {
    for (let i = 0; i < array.length; i++) {
      if (array[i].id === id) {
        return i;
      }
    }
    return -1; // Not found
  }

  checkToDateValidator(control) {
    const fromDate = new Date(control.parent?.get('from').value);
    const toDate = new Date(control.value);
    if (toDate <= fromDate) {
      return { invalidToDate: true };
    }
    return null;
  }
}
